import { useEffect, useMemo, useState } from "react";
import { Modal } from "@/components/ui/modal";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { useI18n } from '@/hooks/useI18n';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';
import { ItineraryActivity } from "../models/activity.model";
import {
  useActivityLibraryQuery,
  useSaveActivityToLibrary,
} from "@/lib/library/hooks/useActivityLibrary";
import { LibraryOwnerType } from "@/lib/library/models/library-activity.model";
import { LibraryActivityFactory } from "@/lib/library/models/library-activity.model";
import { TagInput } from "@/lib/library/components/TagInput";

interface SaveToLibraryModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Attività del builder da salvare in libreria; null quando il modal è chiuso. */
  activity: ItineraryActivity | null;
}

/**
 * Modal per scegliere i tag (e, per un account `agency`, l'ambito di salvataggio)
 * prima di salvare un'attività del builder in libreria. I tag esistenti (derivati
 * dalle entry già presenti in libreria, stesso approccio di LibraryActivitiesTable)
 * sono proposti come chip selezionabili; è comunque possibile crearne di nuovi
 * al volo tramite TagInput.
 *
 * Solo chi ha `userRole === 'agency'` può scegliere di salvare come libreria
 * dell'agenzia (`owner_type='agency'`): per design le RLS `activity_library_insert`
 * ammettono `owner_type='agency'` solo se il chiamante soddisfa `is_agency()` E
 * `owner_id = auth.uid()` — un `travel_advisor_member` non potrà mai scrivere come
 * agenzia, quindi per tutti gli altri ruoli il comportamento resta invariato
 * (sempre `owner_type: 'user'`, nessuna scelta mostrata).
 */
export function SaveToLibraryModal({ isOpen, onClose, activity }: SaveToLibraryModalProps) {
  const { t } = useI18n();
  const { user, userRole } = useAuth();
  const { toast } = useToast();
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const canChooseAgencyScope = userRole === 'agency';
  const [saveScope, setSaveScope] = useState<LibraryOwnerType>('user');

  const { data: libraryActivities = [] } = useActivityLibraryQuery();
  const saveActivityToLibrary = useSaveActivityToLibrary();

  const availableTags = useMemo(() => {
    const tagSet = new Set<string>();
    libraryActivities.forEach((entry) => entry.tags.forEach((tag) => tagSet.add(tag)));
    return Array.from(tagSet).sort();
  }, [libraryActivities]);

  // Riparte da una selezione pulita ogni volta che il modal si riapre per una
  // nuova attività.
  useEffect(() => {
    if (isOpen) {
      setSelectedTags([]);
      setSaveScope('user');
    }
  }, [isOpen, activity?.id]);

  const handleSave = () => {
    if (!activity) return;
    if (!user?.id) {
      console.error('Cannot save activity to library: missing current user id');
      toast({
        title: t('common.error'),
        description: t('library.saveErrorDescription'),
        variant: 'destructive',
      });
      return;
    }

    // Per un account `agency` l'id dell'agenzia coincide col proprio user id
    // (stesso pattern già usato in LibraryPage.tsx per calcolare `agencyId`).
    const ownerType: LibraryOwnerType = canChooseAgencyScope ? saveScope : 'user';

    const payload = LibraryActivityFactory.fromItineraryActivity(activity, { tags: selectedTags });
    saveActivityToLibrary.mutate(
      {
        activity: payload,
        ownerType,
        ownerId: user.id,
      },
      {
        onSuccess: () => onClose(),
      }
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('library.saveToLibraryModalTitle')}
      size="sm"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSave} disabled={!activity || saveActivityToLibrary.isPending}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="p-4 flex flex-col gap-4">
        {activity && (
          <div>
            <p className="text-xs text-gray-500">{t('library.saveToLibraryModalActivityLabel')}</p>
            <p className="font-semibold text-gray-900">{activity.title}</p>
          </div>
        )}

        {canChooseAgencyScope && (
          <div className="space-y-2">
            <p className="text-sm font-medium text-gray-900">{t('library.saveScopeLabel')}</p>
            <RadioGroup
              value={saveScope}
              onValueChange={(value) => setSaveScope(value as LibraryOwnerType)}
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="user" id="save-scope-user" />
                <Label htmlFor="save-scope-user" className="font-normal cursor-pointer">
                  {t('library.saveScopePersonalOption')}
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="agency" id="save-scope-agency" />
                <Label htmlFor="save-scope-agency" className="font-normal cursor-pointer">
                  {t('library.saveScopeAgencyOption')}
                </Label>
              </div>
            </RadioGroup>
          </div>
        )}

        <TagInput
          availableTags={availableTags}
          selectedTags={selectedTags}
          onChange={setSelectedTags}
        />
      </div>
    </Modal>
  );
}
